import { Button, Form, Input, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
    getRetailMatchResultsSuccess,
    reverseGeocodeBenchmarks,
    setRetailMatchBenchmarkRecord,
    updateJob,
} from "../../../../Actions/RetailMatchActions";
import {
    IRetailMatchJobResult,
    IStoreScoreRecord,
} from "../../../../Models/Models";
import { retailMatchReportDataSelector } from "../../../../Reducers/Selectors/RetailMatchSelectors";

interface IRetailMatchEditAddressModalProps {
  visible: boolean;
  defaultAddress: string;
  defaultCity: string;
  defaultState: string;
  defaultZipcode: string;
  hide: () => void;
}

interface IFormItemMetadata {
  label: string;
  value: string;
  validateStatus: "" | "error" | "success" | "warning" | "validating";
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const RetailMatchEditAddressModal = (
    props: IRetailMatchEditAddressModalProps
) => {
    /*********************************
    State
    *********************************/
    const dispatch = useDispatch();
    const { jobResult, benchmarkRecord } = useSelector(
        retailMatchReportDataSelector,
        shallowEqual
    );

    const [address, setAddress] = useState<string>(props.defaultAddress);
    const [city, setCity] = useState<string>(props.defaultCity);
    const [state, setState] = useState<string>(props.defaultState);
    const [zipcode, setZipcode] = useState<string>(props.defaultZipcode);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    /*********************************
    Event Handlers
    *********************************/
    const onAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAddress(event.target.value);
    };

    const onCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCity(event.target.value);
    };

    const onStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState(event.target.value);
    };

    const onZipcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setZipcode(event.target.value);
    };

    const close = () => {
        props.hide();
        setTimeout(resetForm, 500);
    };

    const onSubmit = () => {
        submitUpdate(false);
    };

    const onAutoGenerate = () => {
        submitUpdate(true);
    };

    /*********************************
    Helpers
    *********************************/
    const resetForm = () => {
        setAddress(props.defaultAddress);
        setCity(props.defaultCity);
        setState(props.defaultState);
        setZipcode(props.defaultZipcode);
    };

    const submitUpdate = (autoGenerateAddress: boolean) => {
        const newAddress = autoGenerateAddress ? null : address;
        const newCity = autoGenerateAddress ? null : city;
        const newState = autoGenerateAddress ? null : state;
        const newZipcode = autoGenerateAddress ? null : zipcode;

        const newJobResult: IRetailMatchJobResult = {
            ...jobResult,
            job: {
                ...jobResult.job,
                addressOverride: newAddress,
                cityOverride: newCity,
                stateOverride: newState,
                zipcodeOverride: newZipcode,
            },
        };

        const newBenchmarkRecord: IStoreScoreRecord = {
            ...benchmarkRecord,
            addr: newAddress,
            city: newCity,
            st: newState,
            zip: newZipcode,
        };

        updateJobAndBenchmark(
            newJobResult,
            newBenchmarkRecord,
            autoGenerateAddress
        );
    };

    const updateJobAndBenchmark = (
        jobResult: IRetailMatchJobResult,
        benchmarkRecord: IStoreScoreRecord,
        autoGenerateAddress: boolean
    ) => {
        setIsLoading(true);
        dispatch(
            updateJob(jobResult.job, () => {
                if (autoGenerateAddress) {
                    reverseGeocodeBenchmarks(jobResult, dispatch).then((rgBenchmark) => {
                        dispatch(setRetailMatchBenchmarkRecord(rgBenchmark[0]));
                    });
                } else {
                    dispatch(setRetailMatchBenchmarkRecord(benchmarkRecord));
                }

                dispatch(getRetailMatchResultsSuccess(jobResult));
                setTimeout(() => {
                    close();
                    setIsLoading(false);
                }, 1000);
            })
        );
    };

    const validateStatus = (
        field: string
    ): "" | "error" | "success" | "warning" | "validating" => {
        return field?.trim() !== "" ? "success" : "error";
    };

    /*********************************
    Effects
    *********************************/
    useEffect(resetForm, [props.visible]);

    /*********************************
    Data
    *********************************/
    const formItems: IFormItemMetadata[] = [
        {
            label: "Street Address",
            value: address,
            onChange: onAddressChange,
            validateStatus: validateStatus(address),
        },
        {
            label: "City",
            value: city,
            onChange: onCityChange,
            validateStatus: validateStatus(city),
        },
        {
            label: "State",
            value: state,
            onChange: onStateChange,
            validateStatus: validateStatus(state),
        },
        {
            label: "Zip code",
            value: zipcode,
            onChange: onZipcodeChange,
            validateStatus: validateStatus(zipcode),
        },
    ];

    const formValid = formItems.every((item) => {
        return item.validateStatus === "success";
    });

    return (
        <Modal
            title="Edit address"
            visible={props.visible}
            onOk={onSubmit}
            onCancel={close}
            closable={false}
            maskClosable={false}
            footer={
                <>
                    <Button onClick={close} disabled={isLoading}>
            Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={onAutoGenerate}
                    >
            Find address for me
                    </Button>
                    <Button
                        type="primary"
                        loading={isLoading}
                        disabled={isLoading || !formValid}
                        onClick={onSubmit}
                    >
            Submit my changes
                    </Button>
                </>
            }
        >
            <Form>
                {formItems.map((item) => {
                    return (
                        <Form.Item
                            key={item.label}
                            label={item.label}
                            required={true}
                            validateStatus={item.validateStatus}
                            help={item.validateStatus === "error" ? "Required" : null}
                            style={{
                                marginBottom: "0",
                            }}
                        >
                            <Input
                                disabled={isLoading}
                                value={item.value}
                                onChange={item.onChange}
                            />
                        </Form.Item>
                    );
                })}
            </Form>
        </Modal>
    );
};
